// 审核详情弹窗：给 provider 看的审核报告（不含 AI 内部过程/成本等运维指标）。
// 结论 + 总结 + 测试结果表 + 问题清单 + 文档待补/稳定性 + 改动(diff) + 审核时间。
// 数据来自 GET /api-services/:id/revisions/:revId/review。
import React, { useEffect, useState } from 'react';
import { XPrim } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../../ui/index.js';
import { DiffView, REVIEW_OUTCOME, REVIEW_EXPLAIN } from './version-shared.jsx';

const VERDICT = { PASS: { t: 'PASS', c: '#16a34a' }, FAIL: { t: 'FAIL', c: '#dc2626' }, UNCERTAIN: { t: 'UNCERTAIN', c: '#b45309' } };
const SEVERITY = {
  CRITICAL: { bg: 'rgba(239,68,68,0.18)', fg: '#dc2626' },
  HIGH: { bg: 'rgba(239,68,68,0.12)', fg: '#e06b5e' },
  MEDIUM: { bg: 'rgba(234,179,8,0.14)', fg: '#b45309' },
  LOW: { bg: 'var(--bg-1)', fg: 'var(--fg-3)' },
};

const Chip = ({ bg, fg, children }) => (
  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: bg, color: fg }}>{children}</span>
);
const Section = ({ title, count, children }) => (
  <div style={{ marginTop: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
      {title}{count != null && <span style={{ color: 'var(--fg-4)', fontWeight: 400 }}> · {count}</span>}
    </div>
    {children}
  </div>
);

export const ReviewReportDialog = ({ open, apiId, revId, revLabel, onClose }) => {
  const { toast } = XPrim.useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null); // 选中的提交（时间线）；null = 最新一次

  useEffect(() => {
    if (!open || !apiId || !revId) return;
    let cancelled = false;
    setLoading(true); setData(null); setSelId(null);
    XApi.apiSvc.revisionReview(apiId, revId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) toast({ title: e?.message || '加载审核报告失败', type: 'error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiId, revId]);

  // 一条修订可多次提交审核 → 时间线（最新在前）；选中哪条就展示哪条的完整报告。
  const attempts = data?.attempts ?? (data?.review ? [data.review] : []);
  const r = attempts.find((a) => a.id === selId) ?? attempts[0] ?? null;
  const oc = r ? (REVIEW_OUTCOME[r.outcome] || { text: r.status, bg: 'var(--bg-1)', fg: 'var(--fg-3)' }) : null;
  const vd = r?.verdict ? VERDICT[r.verdict] : null;

  // 审核中：弹窗内轮询刷新（弹窗本身不会自刷新，否则会一直停在 stale「审核中」）。
  const latestOutcome = attempts[0]?.outcome;
  useEffect(() => {
    if (!open || latestOutcome !== 'reviewing') return;
    const t = setInterval(() => {
      XApi.apiSvc.revisionReview(apiId, revId).then((d) => setData(d)).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, latestOutcome, apiId, revId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={680}>
      <DialogHeader title={`审核详情${revLabel ? ` · ${revLabel}` : ''}`} onClose={onClose} />
      <DialogBody>
        {loading ? (
          <div style={{ color: 'var(--fg-3)', padding: 16 }}>加载中…</div>
        ) : !r ? (
          <div style={{ color: 'var(--fg-3)', padding: 16 }}>该修订尚无审核记录。</div>
        ) : (
          <div>
            {/* 提交审核时间线：一条修订可多次提交（驳回→改→重提）。点选查看任一次的报告。 */}
            {attempts.length > 1 && (
              <div style={{ marginBottom: 12, border: '1px solid var(--bd-1)', borderRadius: 6, padding: 8 }}>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>提交审核时间线 · {attempts.length} 次</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {attempts.map((a, i) => {
                    const n = attempts.length - i; // 最新在前，序号从最早=1
                    const aoc = REVIEW_OUTCOME[a.outcome] || { text: a.status, bg: 'var(--bg-1)', fg: 'var(--fg-3)' };
                    const active = a.id === r.id;
                    return (
                      <button key={a.id} type="button" onClick={() => setSelId(a.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', padding: '5px 8px', borderRadius: 5, border: active ? `1px solid ${aoc.fg}66` : '1px solid transparent', background: active ? `${aoc.fg}10` : 'transparent', cursor: 'pointer' }}>
                        <span style={{ fontSize: 11, color: 'var(--fg-3)', fontWeight: active ? 700 : 400, minWidth: 44 }}>第 {n} 次</span>
                        <Chip bg={aoc.bg} fg={aoc.fg}>{aoc.text}</Chip>
                        <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>{a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 结论 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Chip bg={oc.bg} fg={oc.fg}>{oc.text}</Chip>
              {vd && <span style={{ fontSize: 12, fontWeight: 700, color: vd.c }}>{vd.t}</span>}
              {r.confidence != null && <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>置信度 {Math.round(r.confidence * 100)}%</span>}
              {r.reviewerType && <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>{r.reviewerType === 'agent' ? 'Agent 自动审核' : '人工审核'}</span>}
            </div>

            {/* 人话解释：这个状态是什么意思 + 接下来怎么办 */}
            {REVIEW_EXPLAIN[r.outcome] && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: REVIEW_EXPLAIN[r.outcome].fg, background: `${REVIEW_EXPLAIN[r.outcome].fg}14`, border: `1px solid ${REVIEW_EXPLAIN[r.outcome].fg}33`, borderRadius: 6, padding: '8px 10px', lineHeight: 1.6 }}>
                {REVIEW_EXPLAIN[r.outcome].text}
              </div>
            )}

            {/* 审核备注（技术原文）*/}
            {r.note && (
              <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--fg-4)', lineHeight: 1.5 }}>
                审核备注：{r.note}
              </div>
            )}
            {r.summary && typeof r.summary === 'string' && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--fg-2)', lineHeight: 1.6 }}>{r.summary}</div>
            )}

            {/* 测试结果表 */}
            {r.testCases?.length > 0 && (
              <Section title="测试结果" count={r.testCases.length}>
                <div style={{ border: '1px solid var(--bd-1)', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 56px 56px 64px', fontSize: 10.5, color: 'var(--fg-4)', padding: '4px 10px', background: 'var(--bg-1)', borderBottom: '1px solid var(--bd-1)' }}>
                    <span>端点</span><span>期望</span><span>实际</span><span>结果</span>
                  </div>
                  {r.testCases.map((t, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 56px 56px 64px', fontSize: 11.5, padding: '5px 10px', borderBottom: i < r.testCases.length - 1 ? '1px solid var(--bd-1)' : 'none', alignItems: 'center', background: (!t.ok || t.fakeSuccess) ? 'rgba(239,68,68,0.06)' : 'transparent' }}>
                      <span className="mono" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.endpoint}{t.kind === 'edge' && <span style={{ color: 'var(--fg-4)' }}> ·边界</span>}</span>
                      <span style={{ color: 'var(--fg-3)' }}>{t.expectedStatus ?? '—'}</span>
                      <span style={{ color: 'var(--fg-3)' }}>{t.actualStatus ?? '—'}</span>
                      <span style={{ color: t.fakeSuccess ? '#dc2626' : t.ok ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{t.fakeSuccess ? '假成功' : t.ok ? '✓' : '✗'}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* 问题清单 */}
            {r.issues?.length > 0 && (
              <Section title="问题清单" count={r.issues.length}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {r.issues.map((is, i) => {
                    const sv = SEVERITY[is.severity] || SEVERITY.LOW;
                    return (
                      <div key={i} style={{ border: '1px solid var(--bd-1)', borderRadius: 6, padding: '10px 12px' }}>
                        {/* 严重度徽标单独占一行，标题/建议全宽，避免左侧 label 挤压长标题 */}
                        <div><Chip bg={sv.bg} fg={sv.fg}>{is.severity}</Chip></div>
                        <div style={{ fontSize: 12.5, color: 'var(--fg-1)', fontWeight: 500, marginTop: 7, lineHeight: 1.55 }}>{is.description}</div>
                        {is.suggestion && <div style={{ fontSize: 11.5, color: 'var(--fg-3)', marginTop: 5, lineHeight: 1.55 }}>建议：{is.suggestion}</div>}
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* 文档待补 / 不稳定端点 */}
            {(r.indeterminateEndpoints?.length > 0 || r.flakyEndpoints?.length > 0) && (
              <Section title="覆盖说明">
                {r.indeterminateEndpoints?.length > 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>文档不足、无法确定调用方式：{r.indeterminateEndpoints.map((e) => <span key={e} className="mono" style={{ marginRight: 6 }}>{e}</span>)}</div>
                )}
                {r.flakyEndpoints?.length > 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--fg-3)', marginTop: 4 }}>间歇不稳定（可达但偶失）：{r.flakyEndpoints.map((e) => <span key={e} className="mono" style={{ marginRight: 6 }}>{e}</span>)}</div>
                )}
              </Section>
            )}

            {/* 改动 */}
            {r.diff && (
              <Section title="本次改动">
                <DiffView diff={r.diff} />
              </Section>
            )}

            {/* 审核时间（模型/调用数/轮数/成本等内部审计指标不对 provider 展示）*/}
            {r.createdAt && (
              <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--bd-1)', fontSize: 11, color: 'var(--fg-4)' }}>
                审核时间 {new Date(r.createdAt).toLocaleString()}
              </div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn btn-sm" onClick={onClose}>关闭</button>
      </DialogFooter>
    </Dialog>
  );
};

export default ReviewReportDialog;
